import React, { Fragment, useState, useEffect, Component, useCallback } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import StepWizard from 'react-step-wizard';
import transitions from './transitions.less';
import TextField from '@mui/material/TextField';
import isEmail from 'validator/lib/isEmail';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import WarningIcon from '@mui/icons-material/Warning';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { getTimeZones, rawTimeZones, timeZonesNames, abbreviations } from "@vvo/tzdb";
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Loader from './Loader';
import Slider from '@mui/material/Slider';
import InputAdornment from '@mui/material/InputAdornment';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import googlePlayBtn from '../img/google-play-badge.png';
import appStoreBtn from '../img/AppStore.svg';
import webQR from '../img/webQR.svg';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import parsePhoneNumber from 'libphonenumber-js';

// Password validators copied from ActivateAccountWizard
RegExp.escape = function(s) {
  return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};
const hasFourContinuousCharactersOverlap = (str1, str2) => {
  for (let i = 0; i <= (str1 ? str1.length : 0) - 4; i++) {
    const fourCharSubStr = str1.substring(i, i + 4);
    const regex = new RegExp(RegExp.escape(fourCharSubStr), "i");
    if (regex.test(str2)) return true;
  }
  return false;
};
const validatePassword = (password, username) => {
  const lengthCheck = password.length >= 8 && password.length <= 15;
  const upperCaseCheck = /[A-Z]/.test(password);
  const lowerCaseCheck = /[a-z]/.test(password);
  const numberCheck = /[0-9]/.test(password);
  const specialCharCheck = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password);
  const startWithLetterCheck = /^[a-zA-Z]/.test(password);
  const sameCharRepeatedMoreThan4TimesCheck = /([a-zA-Z0-9])\1{4,}/.test(password) || password === "";
  const fourContinuousCharactersOverlapWithUsernameCheck =
    username && password !== "" ? hasFourContinuousCharactersOverlap(username, password) : true;
  return (lengthCheck ? 0x1 : 0x0) |
         (upperCaseCheck ? 0x2 : 0x0) |
         (lowerCaseCheck ? 0x4 : 0x0) |
         (numberCheck ? 0x8 : 0x0) |
         (specialCharCheck ? 0x10 : 0x0) |
         (startWithLetterCheck ? 0x20 : 0x0) |
         (!sameCharRepeatedMoreThan4TimesCheck ? 0x40 : 0x0) |
         (!fourContinuousCharactersOverlapWithUsernameCheck ? 0x80 : 0x0);
};

const useSize = () => {
  const [windowSize, setWindowSize] = useState([window.innerHeight, window.innerWidth]);
  useEffect(() => {
    const windowSizeHandler = () => setWindowSize([window.innerHeight, window.innerWidth]);
    window.addEventListener("resize", windowSizeHandler);
    return () => window.removeEventListener("resize", windowSizeHandler);
  }, []);
  return windowSize;
};

const ResetPasswordWizard = () => {
  // prevent back button during wizard use, consistent with other wizards
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    const handler = () => window.history.go(1);
    window.onpopstate = handler;
    return () => { window.onpopstate = null; };
  }, []);

  const [state, updateState] = useState({
    form: {
      email: '',
      requestStatus: 'none', // none | pending | success | error
      // code-based reset flow -> renamed to token
      token: null,
      tokenStatus: 'none', // none | pending | ok | error | notfound
      activationStatus: 'none', // none | pending | inprocess | success | error
      userDetails: {},
      password: '',
      passwordValStatus: 0x0,
      specificError: null
    }
  });

  const updateForm = (key, value) => {
    const { form } = state;
    form[key] = value;
    updateState({ ...state, form });
  };

  // Capture ?token= from URL
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) updateForm('token', token);
  }, []);

  const onStepChange = () => {
    setTimeout(() => window.scrollTo(0, 0), 300);
  };

  const setInstance = SW => updateState({ ...state, SW });
  const windowsize = useSize();

  const hasToken = !!state.form.token;

  return (
    <div>
      <StepWizard onStepChange={onStepChange} isHashEnabled={false} instance={setInstance}>
        {!hasToken && <RequestStep form={state.form} update={updateForm} windowSize={windowsize} />}
        {!hasToken && <SuccessStep form={state.form} windowSize={windowsize} />}
        {hasToken && <CodeFirst form={state.form} update={updateForm} windowSize={windowsize} />}
        {/* removed CodeSecond to avoid slide-forward during submission */}
      </StepWizard>
    </div>
  );
};

const RequestStep = (props) => {
  const update = (k, v) => props.update(k, v);

  const submit = () => {
    if (!props.form.email || !isEmail(props.form.email)) return;
    update('requestStatus', 'pending');
    fetch(`/cpasrv/api/request_password_reset/${encodeURIComponent(props.form.email)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: null
    })
      .then(res => {
        if (res.ok) {
          update('requestStatus', 'success');
          props.nextStep();
        } else {
          update('requestStatus', 'error');
        }
      })
      .catch(() => update('requestStatus', 'error'));
  };

  const emailValid = props.form.email && isEmail(props.form.email);
  const nextDisabled = !emailValid || props.form.requestStatus === 'pending';
  const formInputProps = { notched: false, style: { fontSize: 20, borderRadius: 12 } };
  const formInputLabelProps = { shrink: true, style: { fontSize: 20, top: -16, left: -14 } };

  return (
    <div>
      <div className="step-title-bg">
        <Typography component="div">
          <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Reset Password</Box>
        </Typography>
      </div>
      <br /><br />
      <div style={{ paddingLeft: 8, paddingRight: 8 }}>
        <div style={{ paddingLeft: 28, paddingRight: 28 }}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'light', fontSize: 20, color: "#5E5E5E" }}>
              Enter your email to request a password reset link.
            </Box>
          </Typography>
          <br /><br />
          <TextField
            autoFocus
            required
            id="email"
            label="EMAIL ADDRESS"
            type="email"
            value={props.form.email || ''}
            error={!!props.form.email && !emailValid}
            onChange={(e) => update('email', e.target.value.replace(/[^a-zA-Z0-9.\-_@]/g, ''))}
            fullWidth
            placeholder="Enter Email Address"
            InputProps={formInputProps}
            InputLabelProps={formInputLabelProps}
          />
          <br /><br /><br />
          <div style={{ width: "100%" }}>
            <button
              className='btn btn-primary btn-block'
              disabled={nextDisabled}
              onClick={submit}
              style={{
                fontSize: 20,
                fontWeight: 600,
                backgroundColor: nextDisabled ? "#BFBFBF" : "green",
                border: nextDisabled ? '1px solid #9F9F9F' : '1px solid green',
                height: '50px',
                width: '100%'
              }}>
              Submit
            </button>
            {props.form.requestStatus === 'error' ? (
              <>
                <br />
                <span className="error-color">An error occurred. Please try again.</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessStep = (props) => {
  // Only render once the email request completed successfully
  if (props.form.requestStatus !== 'success') return null;

  return (
    <div>
      <div className="step-title-bg">
        <Typography component="div">
          <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Check Your Email Inbox</Box>
        </Typography>
      </div>
      <br /><br />
      <div style={{ paddingLeft: 36, paddingRight: 36 }}>
        <Typography component="div">
          <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>We sent a password reset link to</Box>
          <Box sx={{ fontWeight: 'light', fontSize: 20, wordWrap: 'break-word' }}>{props.form.email}</Box>
        </Typography>
        <br />
        <Typography component="div">
          <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Open your email and follow the link to reset your password.</Box>
        </Typography>
      </div>
    </div>
  );
};

// New: code-based password reset steps

const CodeFirst = (props) => {
  const update = (name, value) => props.update(name, value);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (e) => e.preventDefault();

  // Validate token and load user details
  if (props.form.tokenStatus === 'none') {
    const token = props.form.token;
    update('tokenStatus', 'pending');
    fetch(`/cpasrv/api/get_activation_details/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      body: null
    })
      .then(r => r.json())
      .then(res => {
        if (res.error === 'none') {
          update('tokenStatus', 'ok');
          update('userDetails', res.user);
        } else if (res.error.includes('ERR05')) {
          update('tokenStatus', 'notfound');
        } else {
          update('tokenStatus', 'error');
        }
      })
      .catch(() => update('tokenStatus', 'error'));
  }

  if (props.form.tokenStatus === 'pending') {
    return (
      <div>
        <div className="step-title-bg">Validating Link</div>
        <br /><br />
        <div style={{ paddingLeft: 36, paddingRight: 36 }}>
          <Loader />
          <br /><br />
          <Typography component="div">
            <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Please wait...</Box>
          </Typography>
        </div>
      </div>
    );
  } else if (props.form.tokenStatus === 'error') {
    return (
      <div>
        <div className="step-title-err"><WarningIcon style={{ paddingRight: 16, fontSize: "xxx-large" }} />An Error Occurred</div>
        <br /><br />
        <div style={{ paddingLeft: 36, paddingRight: 36 }}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'light', fontSize: 20 }}>We had an issue connecting to our server.</Box>
          </Typography>
          <br />
          <Typography component="div">
            <Box sx={{ fontWeight: 'light', fontSize: 20 }}>Please try the link again or request a new password reset email.</Box>
          </Typography>
        </div>
      </div>
    );
  } else if (props.form.tokenStatus === 'notfound') {
    return (
      <div>
        <div className="step-title-err"><WarningIcon style={{ paddingRight: 16, fontSize: "xxx-large" }} />Invalid or Expired Link</div>
        <br /><br />
        <div style={{ paddingLeft: 36, paddingRight: 36 }}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'light', fontSize: 20 }}>Your password reset link has expired or is not valid.</Box>
          </Typography>
          <br />
          <Typography component="div">
            <Box sx={{ fontWeight: 'light', fontSize: 20 }}>
              Please request a new password reset email <a href="/reset_password">here</a>.
            </Box>
          </Typography>
        </div>
      </div>
    );
  } else if (props.form.tokenStatus === 'ok' && props.form.activationStatus === 'success') {
    return (
      <div>
        <div className="step-title-bg">Password Reset Successful</div>
        <br /><br />
        <div style={{ paddingLeft: 36, paddingRight: 36 }}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Your password has been updated for</Box>
            <Box sx={{ fontWeight: 'light', fontSize: 20, wordWrap: 'break-word' }}>{props.form.userDetails.email}</Box>
          </Typography>
          <br />
          <Typography component="div">
            <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>
              You can now sign in with your new password <a href="/login">here</a>.
            </Box>
          </Typography>
        </div>
      </div>
    );
  } else if (props.form.tokenStatus === 'ok') {
    const formInputProps = {
      notched: false,
      style: { fontSize: 20, borderRadius: 12 },
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            edge="end"
          >
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </InputAdornment>
      )
    };
    const formInputLabelProps = { shrink: true, style: { fontSize: 20, top: -16, left: -14, color: "#353535", fontWeight: 600 } };
    const greencheck = <CheckCircleIcon sx={{ color: 'green' }} />;
    const circle = <RadioButtonUncheckedIcon />;

    const submitting = props.form.activationStatus === 'pending' || props.form.activationStatus === 'inprocess';
    const nextDisabled = submitting || !(props.form.password && props.form.passwordValStatus === 0xff);

    const submitPassword = async () => {
      if (nextDisabled) return;
      update('activationStatus', 'pending');
      update('specificError', null);
      try {
        const response = await fetch(`/cpasrv/api/set_password/${encodeURIComponent(props.form.userDetails.email)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(props.form)
        });
        if (response.ok) {
          update('activationStatus', 'success');
        } else {
          let message = null;
          try {
            const data = await response.json();
            message = data?.message || null;
          } catch (_) {}
          update('specificError', message);
          update('activationStatus', 'error');
        }
      } catch (_) {
        update('activationStatus', 'error');
        // optionally set a fallback message if needed:
        // update('specificError', 'Network error. Please try again.');
      }
    };

    return (
      <div>
        <div className="step-title-bg">
          <Typography component="div">
            <Box sx={{ fontWeight: 'medium', fontSize: 20 }}>Reset Password</Box>
          </Typography>
        </div>
        <br /><br />
        <div style={{ paddingLeft: 8, paddingRight: 8, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ paddingLeft: 8, paddingRight: 8 }}>
            <Typography component="div">
              <Box sx={{ fontWeight: 'light', fontSize: 20, color: "#5E5E5E" }}>
                {props.form.userDetails.firstName ? `Welcome ${props.form.userDetails.firstName}!` : null}
              </Box>
            </Typography>
            <br /><br /><br />
            <TextField
              autoFocus
              id="password-required"
              label="CREATE YOUR PASSWORD"
              type={showPassword ? 'text' : 'password'}
              onChange={e => {
                update('password', e.target.value);
                update('passwordValStatus', validatePassword(e.target.value, props.form.userDetails.email));
                if (props.form.activationStatus === 'error') {
                  update('activationStatus', 'none');
                  update('specificError', null);
                }
              }}
              fullWidth
              placeholder="Enter password"
              InputProps={formInputProps}
              InputLabelProps={formInputLabelProps}
            />
            <br /><br />
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{props.form.passwordValStatus & 0x20 ? greencheck : circle} Starts with a letter</div>
            </div>
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{(props.form.passwordValStatus & 0x4) && (props.form.passwordValStatus & 0x2) ? greencheck : circle} At least one uppercase & lowercase letter</div>
            </div>
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{(props.form.passwordValStatus & 0x10) && (props.form.passwordValStatus & 0x8) ? greencheck : circle} At least one special character & one number</div>
            </div>
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{props.form.passwordValStatus & 0x1 ? greencheck : circle} 8 to 15 characters</div>
            </div>
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{props.form.passwordValStatus & 0x40 ? greencheck : circle} Same character not repeated more than 4x</div>
            </div>
            <div style={{ display: 'flex', fontSize: 14, color: "#5E5E5E" }}>
              <div>{props.form.passwordValStatus & 0x80 ? greencheck : circle} Four characters of email not included</div>
            </div>
            <br />
            <div style={{ width: "100%" }}>
              <button
                className='btn btn-primary btn-block'
                disabled={nextDisabled}
                onClick={submitPassword}
                style={{
                  fontSize: 20,
                  backgroundColor: nextDisabled ? "#BFBFBF" : "green",
                  border: nextDisabled ? '1px solid #9F9F9F' : '1px solid green',
                  height: '50px',
                  width: '100%',
                  fontWeight: 600
                }}
              >
                {submitting ? 'Submitting...' : 'Create Password'}
              </button>
              {props.form.activationStatus === 'error' ? (
                <>
                  <br />
                  <span className="error-color">An error occurred updating your password. Please try again.</span>
                  {props.form.specificError ? (
                    <>
                      <br />
                      <span className="error-color">{props.form.specificError}</span>
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default ResetPasswordWizard;
